import React, { useEffect, useMemo, useState } from "react";
import { sdh_rules, type EnrichedRule } from "../api/sdhAPI";
import { RuleTagBadges, DIM_TONE, dimensionOf } from "./HelpKit";
import { measurementOf } from "./measurement";

/**
 * Approved-rules KPI dashboard. Reads the normalized tags (execution_state, dimension) and rolls them
 * up into pass/flag/not-measured KPIs and a by-dimension breakdown. Every tile and dimension row is a
 * filter: clicking one narrows the rule list at the bottom; clicking a rule opens its details.
 */

type ExecKey = "passed" | "flags" | "notmeasured" | "all";
const DIM_HEX: Record<string, string> = {
  Completeness: "#2f7a4f", Validity: "#43607e", Conformity: "#586675",
  Consistency: "#0b5f5e", Integrity: "#946011", Uniqueness: "#8a6d1c",
};
const num = (n: any) => (Number.isFinite(+n) ? (+n).toLocaleString() : "0");
// One source of truth: a rule counts as passed/flagged ONLY when its numbers belong to the version on
// screen. Anything stale, uncompilable, unmeasured or errored is "not measured" — never folded into the
// pass rate, which would report a false headline.
const execOf = (r: any) => {
  const m = measurementOf(r);
  if (!m.ok) return "notmeasured";
  return (m.violations || 0) > 0 ? "flags" : "passed";
};

export default function DashboardTab(
  { entityId, onOpen }: { entityId: string; onOpen: (ruleId: string) => void }
) {
  const [rules, setRules] = useState<EnrichedRule[]>([]);
  const [scope, setScope] = useState<"Approved" | "All">("Approved");
  const [exec, setExec] = useState<ExecKey>("all");
  const [dim, setDim] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => { (async () => {
    setBusy(true);
    try { const r = await sdh_rules(entityId, "All"); setRules(r.rules || []); }
    catch { setRules([]); } finally { setBusy(false); }
  })(); }, [entityId]);

  const lifeOf = (r: any) =>
    (({ AUTO_APPROVED: "Approved", SME_APPROVED: "Approved", REJECTED: "Rejected", PENDING: "Draft" } as any)[r.approval_status || ""]
      || r.lifecycle || "Draft");
  const pool = useMemo(
    () => rules.filter((r) => scope === "All" || lifeOf(r) === "Approved"),
    [rules, scope]);

  const k = useMemo(() => {
    const o = { total: pool.length, passed: 0, flags: 0, notmeasured: 0 };
    pool.forEach((r) => { (o as any)[execOf(r)]++; });
    return o;
  }, [pool]);
  const measured = k.passed + k.flags;
  const passRate = measured ? Math.round((k.passed / measured) * 100) : null;

  const byDim = useMemo(() => {
    const m: Record<string, { passed: number; flags: number; notmeasured: number; total: number }> = {};
    pool.forEach((r) => {
      const d = dimensionOf(r);
      const e = execOf(r);
      (m[d] = m[d] || { passed: 0, flags: 0, notmeasured: 0, total: 0 });
      (m[d] as any)[e]++; m[d].total++;
    });
    return m;
  }, [pool]);

  const shown = useMemo(() => pool.filter((r) =>
    (exec === "all" || execOf(r) === exec) &&
    (!dim || dimensionOf(r) === dim)), [pool, exec, dim]);

  const Tile = ({ id, label, val, tone }: { id: ExecKey; label: string; val: number; tone: string }) => (
    <button className={"db-tile" + (exec === id ? " on" : "")} style={{ ["--t" as any]: tone }}
      onClick={() => setExec(exec === id ? "all" : id)}>
      <span className="v">{num(val)}</span><span className="l">{label}</span>
    </button>
  );

  if (busy) return <div className="db-loading">Loading measurement results…</div>;

  return (
    <div className="db">
      <style>{CSS}</style>

      <div className="db-top">
        <div className="db-scope">
          {(["Approved", "All"] as const).map((s) => (
            <button key={s} className={"db-seg" + (scope === s ? " on" : "")} onClick={() => setScope(s)}>
              {s === "Approved" ? "Approved rules" : "All rules"}
            </button>
          ))}
        </div>
        <div className="db-passrate">
          <div className="ring" style={{ ["--p" as any]: `${passRate ?? 0}%` }}>
            <span>{passRate == null ? "—" : passRate + "%"}</span>
          </div>
          <div className="lab"><b>Pass rate</b>
            <small>{num(k.passed)} of {num(measured)} measured rules pass</small>
            {k.notmeasured > 0 && <small className="warn">
              {num(k.notmeasured)} rule(s) excluded — not measured against their current version
            </small>}
          </div>
        </div>
      </div>

      <div className="db-tiles">
        <Tile id="all" label="rules" val={k.total} tone="#0e2436" />
        <Tile id="passed" label="passed clean" val={k.passed} tone="#2f7a4f" />
        <Tile id="flags" label="flag data" val={k.flags} tone="#b7791f" />
        <Tile id="notmeasured" label="not measured" val={k.notmeasured} tone="#6b7c8d" />
      </div>

      <div className="db-card">
        <div className="db-h">By quality dimension <span className="mut">— click a row to filter</span></div>
        {Object.keys(byDim).sort((a, b) => byDim[b].total - byDim[a].total).map((d) => {
          const row = byDim[d]; const w = (n: number) => `${(n / row.total) * 100}%`;
          return (
            <div key={d} className={"db-dim" + (dim === d ? " on" : "")} onClick={() => setDim(dim === d ? null : d)}>
              <div className="nm"><span className="dot" style={{ background: DIM_HEX[d] || "#6b7c8d" }} />{d}<span className="ct">{row.total}</span></div>
              <div className="bar">
                {row.passed > 0 && <i className="p" style={{ width: w(row.passed) }} title={`${row.passed} passed`} />}
                {row.flags > 0 && <i className="f" style={{ width: w(row.flags) }} title={`${row.flags} flag data`} />}
                {row.notmeasured > 0 && <i className="n" style={{ width: w(row.notmeasured) }} title={`${row.notmeasured} not measured`} />}
              </div>
              <div className="mini"><em className="p">{row.passed}</em><em className="f">{row.flags}</em><em className="n">{row.notmeasured}</em></div>
            </div>
          );
        })}
        <div className="db-legend"><span><i className="p" />passed</span><span><i className="f" />flags data</span><span><i className="n" />not measured</span></div>
      </div>

      <div className="db-card">
        <div className="db-h">
          Rules <span className="mut">{shown.length} shown</span>
          {(exec !== "all" || dim) && <button className="db-clear" onClick={() => { setExec("all"); setDim(null); }}>clear filter</button>}
        </div>
        {shown.length === 0 ? <div className="db-empty">No rules match this selection.</div> :
          shown.slice(0, 300).map((r) => (
            <div key={(r as any).rule_id} className="db-rule" onClick={() => onOpen((r as any).rule_id)}>
              <div className="rn">
                <div className="t">{(r as any).rule_name || (r as any).rule_id}</div>
                <div className="f">{(r as any).owner_field_label || (r as any).owner_field || ""}</div>
              </div>
              <RuleTagBadges rule={r} />
            </div>
          ))}
      </div>
    </div>
  );
}

const CSS = `
.db{--ink:#0E2436;--ink2:#33475b;--muted:#6b7c8d;--line:#e3e8ee;--paper:#f5f7f9;--card:#fff;
 --disp:'Space Grotesk',system-ui,sans-serif;--mono:'JetBrains Mono',ui-monospace,monospace;color:var(--ink)}
.db-loading,.db-empty{padding:34px;text-align:center;color:var(--muted)}
.db-top{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:14px;flex-wrap:wrap}
.db-scope{display:flex;gap:4px;background:var(--paper);border:1px solid var(--line);border-radius:10px;padding:3px}
.db-seg{all:unset;cursor:pointer;padding:6px 14px;border-radius:8px;font-size:13px;color:var(--ink2)}
.db-seg.on{background:var(--card);color:var(--ink);font-weight:600;box-shadow:0 1px 2px rgba(16,36,54,.08)}
.db-passrate{display:flex;align-items:center;gap:12px}
.db-passrate .ring{width:64px;height:64px;border-radius:50%;display:grid;place-items:center;
 background:conic-gradient(#1f9d6b var(--p),#eef1f4 0);font-family:var(--mono)}
.db-passrate .ring span{width:48px;height:48px;border-radius:50%;background:#fff;display:grid;place-items:center;font-size:14px;font-weight:700;color:var(--ink)}
.db-passrate .lab b{font-family:var(--disp);font-size:14px} .db-passrate .lab small{display:block;color:var(--muted);font-size:12px}
.db-passrate .lab small.warn{color:#946011}
.db-tiles{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px}
.db-tile{all:unset;cursor:pointer;background:var(--card);border:1px solid var(--line);border-radius:12px;padding:16px 18px;
 position:relative;transition:box-shadow .15s,border-color .15s}
.db-tile::before{content:"";position:absolute;left:0;top:12px;bottom:12px;width:3px;border-radius:3px;background:var(--t)}
.db-tile:hover{box-shadow:0 4px 14px rgba(16,36,54,.07)}
.db-tile.on{border-color:var(--t);box-shadow:0 0 0 2px color-mix(in srgb,var(--t) 22%,#fff)}
.db-tile .v{display:block;font-family:var(--mono);font-size:26px;font-weight:600;color:var(--t)}
.db-tile .l{font-size:11.5px;color:var(--muted);text-transform:uppercase;letter-spacing:.05em}
.db-card{background:var(--card);border:1px solid var(--line);border-radius:12px;padding:14px 16px;margin-bottom:14px}
.db-h{font-size:12.5px;font-weight:700;color:var(--ink);margin-bottom:10px;display:flex;align-items:center;gap:8px}
.db-h .mut{font-weight:400;color:var(--muted);font-size:11.5px}
.db-clear{all:unset;cursor:pointer;margin-left:auto;font-size:12px;color:#0e7c7b}.db-clear:hover{text-decoration:underline}
.db-dim{display:grid;grid-template-columns:170px 1fr 84px;align-items:center;gap:12px;padding:7px 8px;border-radius:8px;cursor:pointer}
.db-dim:hover{background:var(--paper)} .db-dim.on{background:#eef2f6}
.db-dim .nm{display:flex;align-items:center;gap:7px;font-size:13px;font-weight:600}
.db-dim .nm .dot{width:9px;height:9px;border-radius:50%} .db-dim .nm .ct{margin-left:auto;font-family:var(--mono);font-size:11px;color:var(--muted)}
.db-dim .bar{height:16px;border-radius:5px;overflow:hidden;display:flex;background:#eef1f4}
.db-dim .bar i{height:100%} .db-dim .bar i.p{background:#3f9d6b} .db-dim .bar i.f{background:#d79a3a} .db-dim .bar i.n{background:#c2ccd6}
.db-dim .mini{display:flex;gap:8px;justify-content:flex-end;font-family:var(--mono);font-size:11px}
.db-dim .mini em{font-style:normal} .db-dim .mini em.p{color:#2f7a4f} .db-dim .mini em.f{color:#a06d15} .db-dim .mini em.n{color:#8a97a3}
.db-legend{display:flex;gap:16px;margin-top:8px;padding-top:8px;border-top:1px solid var(--line);font-size:11.5px;color:var(--muted)}
.db-legend span{display:inline-flex;align-items:center;gap:5px} .db-legend i{width:10px;height:10px;border-radius:3px;display:inline-block}
.db-legend i.p{background:#3f9d6b} .db-legend i.f{background:#d79a3a} .db-legend i.n{background:#c2ccd6}
.db-rule{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 8px;border-top:1px solid #eef1f4;cursor:pointer;border-radius:6px}
.db-rule:first-of-type{border-top:none}.db-rule:hover{background:var(--paper)}
.db-rule .rn .t{font-weight:600;font-size:13.5px;color:var(--ink)}
.db-rule .rn .f{font-size:11.5px;color:var(--muted);font-family:var(--mono)}
@media(max-width:820px){.db-tiles{grid-template-columns:repeat(2,1fr)}.db-dim{grid-template-columns:130px 1fr}.db-dim .mini{display:none}}
`;
